<script setup lang="ts">
/*
 * FILE: unified_ui/src/components/Toast.vue
 * PURPOSE: Pure renderer of the global toast store. Mounted once
 *          alongside <CompletionToast /> in App.vue, so any view can
 *          call `useToastStore().show('…')` without owning a UI slot.
 *          Glass-style card, centered horizontally near the top,
 *          pointer-events pass through the container.
 * OWNS: nothing — the store is the source of truth.
 */
import { useToastStore } from '@/stores/toast'

const toast = useToastStore()
</script>

<template>
  <Transition name="toast">
    <div
      v-if="toast.message"
      class="fixed inset-x-0 top-0 z-50 pointer-events-none pt-safe"
    >
      <div class="pointer-events-auto mx-auto max-w-md px-4 pt-4">
        <div
          class="card-heavy flex items-center gap-3 shadow-glass-lg animate-toast-in"
          role="status"
          aria-live="polite"
        >
          <!-- check icon -->
          <svg
            class="h-5 w-5 shrink-0 text-emerald-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <p class="flex-1 text-sm font-medium text-ink-800 dark:text-slate-100 leading-snug">
            {{ toast.message }}
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.animate-toast-in {
  animation: toast-slide-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes toast-slide-in {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.toast-enter-active {
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
